import { AssetVizController } from 'features/assetViz'
import React from 'react'
import {
  GeckoComponentModel,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoLegacyCone,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  GeckoTankFlatRoof,
  GeckoTankFloor,
} from 'types'

export interface CaptureScreenshotOptions {
  /** The asset visualization controller ref */
  assetVizRef: React.RefObject<AssetVizController | null>
  /** The bounds to capture */
  bounds: GeckoComponentRect
  /** Zoom factor for padding (proportion of whitespace) */
  zoomFactor: number
  /** Optional delay before calculating bounds and zooming (in ms) */
  preRenderDelay?: number
  /** Delay after zooming before taking screenshot (in ms, default: 1000) */
  renderDelay?: number
  /** Optional component model - used to handle circular component bounds */
  componentModel?: GeckoComponentModel
}

const CIRCULAR_COMPONENT_TYPES = new Set([
  GeckoComponentModelType.TankFloor,
  GeckoComponentModelType.TankFlatRoof,
  GeckoComponentModelType.FlatCircularFloor,
  GeckoComponentModelType.Dome,
  GeckoComponentModelType.LegacyCone,
])

function isCircularComponent(type?: GeckoComponentModelType): boolean {
  return type !== undefined && CIRCULAR_COMPONENT_TYPES.has(type)
}

function hasPolarCoordinates(bounds: GeckoComponentRect): boolean {
  return (
    bounds.x.unit === GeckoMeasurementUnit.Degrees ||
    bounds.x.unit === GeckoMeasurementUnit.Radians
  )
}

function getCircularComponentRadius(
  componentModel: GeckoComponentModel
): GeckoMeasurement | undefined {
  switch (true) {
    case componentModel instanceof GeckoTankFlatRoof:
      return componentModel.radius
    case componentModel instanceof GeckoTankFloor:
      return componentModel.radius
    case componentModel instanceof GeckoLegacyCone:
      return componentModel.outerRadius
    default:
      return undefined
  }
}

/**
 * Captures a screenshot of the asset visualization for the given bounds.
 *
 * @param options - Configuration options for screenshot capture
 * @returns A promise that resolves to the screenshot blob, or undefined if capture fails
 *
 * @example
 * ```typescript
 * const blob = await captureAssetScreenshot({
 *   assetVizRef,
 *   bounds: componentBounds,
 *   zoomFactor: 0.15,
 *   renderDelay: 1000
 * })
 * ```
 */
export async function captureAssetScreenshot({
  assetVizRef,
  bounds,
  zoomFactor,
  preRenderDelay,
  componentModel,
}: CaptureScreenshotOptions): Promise<Blob | undefined> {
  if (!assetVizRef?.current) {
    return undefined
  }

  if (preRenderDelay) {
    await new Promise((resolve) => setTimeout(resolve, preRenderDelay))
  }

  let effectiveBounds = bounds

  // For circular components with polar coordinate bounds, create Cartesian bounds centered at origin
  if (
    componentModel &&
    isCircularComponent(componentModel.type) &&
    hasPolarCoordinates(bounds)
  ) {
    const radius = getCircularComponentRadius(componentModel)
    if (radius) {
      effectiveBounds = GeckoComponentRect.CreateFromXYs(
        bounds.componentID,
        -radius.num,
        -radius.num,
        radius.num,
        radius.num,
        radius.unit,
        radius.unit
      )
    }
  }

  const xGap = effectiveBounds.x.delta.scale(zoomFactor)
  const yGap = effectiveBounds.y.delta.scale(zoomFactor)

  const outerBounds = GeckoComponentRect.CreateFromXYs(
    effectiveBounds.componentID,
    effectiveBounds.x.start.minus(xGap).num,
    effectiveBounds.y.start.minus(yGap).num,
    effectiveBounds.x.end.plus(xGap).num,
    effectiveBounds.y.end.plus(yGap).num,
    effectiveBounds.x.unit,
    effectiveBounds.y.unit
  )

  const blob = await assetVizRef.current!.zoomToFit(outerBounds).then(() => {
    return assetVizRef.current!.takeScreenshot()
  })

  return blob
}
